import type { LscKTagGuidelineEntry } from "@services/somTagsQuery";

/** True when the LSC guideline entry has a non-empty PDF file URL (after stripping `#…`). */
export function lscKTagEntryHasPdfUrl(entry: LscKTagGuidelineEntry): boolean {
  const g = entry.tag_guideline;
  const raw = g.cms_official_url?.trim() || g.cms_som_pdf_url?.trim() || "";
  if (!raw) return false;
  const stripped = raw.replace(/#.*$/, "");
  return stripped.length > 0;
}

/**
 * 1-based PDF page from `pdf_open_fragment` or URL hash (e.g. `#page=34`, `&page=12`).
 * CMS image PDFs often omit `pdf_page_hint` but include this in the link.
 */
export function parsePdfPageFromOpenParams(
  fullUrl: string,
  fragment: string | null | undefined,
): number | null {
  const trySegment = (segment: string): number | null => {
    const s = segment.trim();
    if (!s) return null;
    const pageParam = s.match(/(?:^|[?#&])page=(\d{1,4})/i);
    if (pageParam) return parseInt(pageParam[1], 10);
    const bare = s.replace(/^#/, "");
    if (/^\d{1,4}$/.test(bare)) return parseInt(bare, 10);
    return null;
  };
  const fromFragment = trySegment(fragment ?? "");
  if (fromFragment != null) return fromFragment;
  const hashIdx = fullUrl.indexOf("#");
  if (hashIdx >= 0) {
    return trySegment(fullUrl.slice(hashIdx));
  }
  return null;
}

export function resolveCmsPdfInitialPage(
  pdfPageHint: number | null | undefined,
  cmsUrl: string,
  pdfOpenFragment: string | null | undefined,
): { pageIndex0: number; source: "hint" | "fragment" | "default" } {
  if (pdfPageHint != null && pdfPageHint >= 1) {
    return {
      pageIndex0: Math.max(0, Math.floor(pdfPageHint) - 1),
      source: "hint",
    };
  }
  const fromLink = parsePdfPageFromOpenParams(cmsUrl, pdfOpenFragment);
  if (fromLink != null && fromLink >= 1) {
    return { pageIndex0: Math.max(0, fromLink - 1), source: "fragment" };
  }
  return { pageIndex0: 0, source: "default" };
}
